import React, { PropsWithChildren, useCallback, useState } from 'react';
import get from 'lodash/get';
import { styled } from 'styled-components';
import { YColumnsType } from '@ydistri/ds';
import { extractData } from '../../../lib/tables/tablesLib';

const ChildrenWrapper = styled.div<{ $isLoading?: boolean }>`
  cursor: ${props => (props.$isLoading ? 'wait' : 'pointer')};
  opacity: ${props => (props.$isLoading ? 0.6 : 1)};
`;

interface TableExportProps<T> extends PropsWithChildren {
  dataSource: T[];
  columns: YColumnsType<T>[];
  fileName: string;
  sheetName: string;
  level?: number;
}

const TableExport = <T extends object>({
  dataSource,
  columns,
  fileName,
  sheetName,
  children,
  level = 1,
}: TableExportProps<T>): React.ReactElement => {
  const [isLoading, setIsLoading] = useState(false);

  const exportData = useCallback(
    async (sheetName: string, fileName: string) => {
      setIsLoading(true);
      try {
        // Lazy load xlsx library only when export is triggered
        const XLSX = await import('xlsx');

        const wb = XLSX.utils.book_new();

        const columnTitles = columns.map(column => column.title);
        const ws = XLSX.utils.aoa_to_sheet([columnTitles]);

        const data = extractData(dataSource, level).map(row => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- we do not care about the return type actually
          return columns.map((column, index) => {
            const dataIndex = get(column, ['dataIndex']);
            if (dataIndex && (typeof dataIndex === 'string' || Array.isArray(dataIndex))) {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- we get a value from the row whatever it is
              const value = get(row, dataIndex);

              if (column.exportRender) {
                return column.exportRender(value, row, index);
              }
              if (column.render) {
                return column.render(value, row, index);
              }
              // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- we return a value from the row whatever it is
              return value ?? '';
            }

            return '';
          });
        });

        //enable filter for all columns
        const filterRange = XLSX.utils.encode_range({
          s: { c: 0, r: 0 },
          e: { c: columnTitles.length - 1, r: data.length },
        });
        XLSX.utils.encode_col(columnTitles.length - 1);
        const ref = filterRange;
        ws['!autofilter'] = {
          ref,
        };

        if (data.length > 0) {
          XLSX.utils.sheet_add_aoa(ws, data, { origin: 'A2' });

          //format numbers
          data.forEach((row, rowIndex) => {
            row.forEach((cell, cellIndex) => {
              if (typeof cell === 'number') {
                const columnDef = columns[cellIndex];
                if (columnDef.exportFormat) {
                  const cellAddress = XLSX.utils.encode_cell({ r: rowIndex + 1, c: cellIndex }); //+1 for the header row
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- its ok here
                  ws[cellAddress].z = columnDef.exportFormat;
                }
              }
            });
          });

          ws['!cols'] = columns.map((column, index: number) => {
            const maxLength = data.reduce((acc, row) => {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-assignment -- we need it here
              const length: number = row[index]?.toString().length ?? 0;
              return length > acc ? length : acc;
            }, 0);

            const columnTitleLength = column.title?.toString().length ?? 0;
            const props = {
              wch: maxLength > columnTitleLength ? maxLength : columnTitleLength + 4,
            };
            if (props.wch < 5) {
              props.wch = 5;
            }

            return props;
          });
        }

        XLSX.utils.book_append_sheet(wb, ws, sheetName);
        XLSX.writeFileXLSX(wb, fileName);
      } catch (error) {
        // eslint-disable-next-line no-console -- Error logging for failed export
        console.error('Failed to export data:', error);
        // Optionally show user-friendly error message
      } finally {
        setIsLoading(false);
      }
    },
    [columns, dataSource, level],
  );

  const clickHandler: React.MouseEventHandler = useCallback(
    event => {
      event.stopPropagation();
      // Call async export function
      void exportData(sheetName, fileName);
    },
    [exportData, fileName, sheetName],
  );

  return (
    <ChildrenWrapper onClick={clickHandler} $isLoading={isLoading}>
      {children}
    </ChildrenWrapper>
  );
};

export default TableExport;
